import {
  Breadcrumbs,
  Container,
  Stack,
  Typography,
  Link,
  Grid,
  useMediaQuery,
  Box,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { StepsData } from "../../../core/models/managements/managements.model";
import "./ServicesUpdateHeader.scss";
import { ManagementUpdateCup } from "../../../core/models/cupsCifSelector/cupsCifSelector.model";
import { SuppliesDetailObject } from "../../../core/models/suppliesDetail/suppliesDetail.model";
import EnvirormentContent from "../../EnvirormentContent/EnvirormentContent";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import { DetailCupsData } from "../../../core/models/managementsUpdate/managementsUpdatePowers/managementsUpdatePowers";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";

export interface ServicesUpdateHeaderProps {
  title: string;
  step: number;
  isLoading?: boolean;
  cupsDetail?: Partial<SuppliesDetailObject>;
  cupsSelected?: ManagementUpdateCup[];
  showStepper?: boolean;
  showDetailCups?: boolean;
  ownerDataCup?: OwnerDataCups;
}

const ServicesUpdateHeader = ({
  title,
  step,
  cupsDetail,
  cupsSelected,
  isLoading = false,
  showStepper = true,
  showDetailCups = false,
  ownerDataCup,
}: ServicesUpdateHeaderProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const numberCupsSelected = cupsSelected?.length;
  const hasDetailCups = cupsDetail?.cupsCode !== "-" && showDetailCups;

  const stepsData: StepsData[] = [
    {
      stepNumber: 0,
      id: "request",
      label: "Solicitud",
    },
    {
      stepNumber: 1,
      id: "processRequest",
      label: "Procesar su solicitud",
    },
    {
      stepNumber: 2,
      id: "resolution",
      label: "Resolución",
    },
  ];

  const detailCupsData: DetailCupsData[] = [
    {
      id: "suppliesDirection",
      label: "Facturas Página.Dirección del suministro",
      value: ["-", ","].some((stringElement) =>
        cupsDetail?.address?.includes(stringElement)
      )
        ? `${cupsDetail?.address}`
        : `${cupsDetail?.address}, ${cupsDetail?.addressNumber}`,
    },
    { id: "cups", label: "Tablas.CUPS", value: cupsDetail?.cupsCode || "-" },
    {
      id: "service",
      label: "Tablas.Servicio",
      value: cupsDetail?.service || "-",
      renderContent: (service: string) => (
        <EnvirormentContent service={service} variant={"subtitle1"} />
      ),
    },
    {
      id: "rate",
      label: "Tablas.Tarifa",
      value: cupsDetail?.rate || "-",
    },
  ];

  const isMobileViewport = useMediaQuery("(max-width:767px)");
  const isDesktopViewport = useMediaQuery("(min-width:768px)");

  const CustomStepIcon = ({
    stepNumber,
    active,
    completed,
  }: {
    stepNumber: number;
    active: boolean;
    completed: boolean;
  }) => {
    if (completed) {
      return <span className="stepper-icon completed t-icon-check" />;
    }
    if (active) {
      return <span className="stepper-icon active">{stepNumber}</span>;
    }
    return <span className="stepper-icon">{stepNumber}</span>;
  };

  return (
    <Stack
      direction="column"
      rowGap={{ xs: "24px", md: "40px" }}
      component="article"
      className="managements-header"
    >
      {isDesktopViewport && (
        <Container>
          <Breadcrumbs aria-label="breadcrumb">
            {!!ownerDataCup?.codeCups.length && (
              <Link
                component="button"
                underline="hover"
                onClick={() => navigate(process.env.REACT_APP_SUPPLIES!)}
                variant="caption"
              >
                {t("Suministros.Suministros")}
              </Link>
            )}
            {!!ownerDataCup?.codeCups.length && (
              <Link
                component="button"
                underline="hover"
                onClick={() =>
                  navigate(
                    `${process.env.REACT_APP_SUPPLIES}/${ownerDataCup.idCups}`
                  )
                }
                variant="caption"
              >
                {`${t("Tablas.CUPS")} ${ownerDataCup.codeCups}`}
              </Link>
            )}
            <Link
              component="button"
              underline="hover"
              onClick={() => navigate(process.env.REACT_APP_SERVICES!)}
              variant="caption"
            >
              {t("Header.Nav.Servicios")}
            </Link>
            <Typography component="p" variant="caption">
              {t(`Servicios.${title}`)}
            </Typography>
          </Breadcrumbs>
        </Container>
      )}
      {isMobileViewport && (
        <Container>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              component="button"
              underline="hover"
              onClick={() => navigate(process.env.REACT_APP_MANAGEMENT!)}
              variant="button"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Typography
                component={"span"}
                aria-hidden={true}
                className="t-icon-long-arrow-left"
                sx={{ mr: "4px" }}
              />
              {t("Volver")}
            </Link>
          </Breadcrumbs>
        </Container>
      )}
      <Container className="managements-header__title-container">
        <Grid container alignItems="center" rowGap={5}>
          <Grid item xs={12} lg={step !== -1 ? 6 : 12}>
            <Typography
              variant={step !== -1 ? "h2" : "h1"}
              component={step !== -1 ? "h2" : "h1"}
            >
              {t(`Servicios.${title}`)}
            </Typography>
          </Grid>
          <Grid item xs={12} lg={6}>
            {step !== -1 && step !== -2 && showStepper && (
              <Box sx={{ width: "100%" }}>
                <Stepper activeStep={step} className="stepper">
                  {stepsData.map(({ stepNumber, id, label }) => (
                    <Step key={id}>
                      <StepLabel
                        StepIconComponent={(props) => (
                          <CustomStepIcon
                            {...props}
                            stepNumber={stepNumber + 1}
                            active={step === stepNumber}
                            completed={step > stepNumber}
                          />
                        )}
                      >
                        {(step === stepNumber || !isMobileViewport) && (
                          <Typography
                            component="span"
                            variant="body2"
                            className={
                              step === stepNumber
                                ? "stepper-label active"
                                : "stepper-label"
                            }
                          >
                            {t(`Gestiones.${label}`)}
                          </Typography>
                        )}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}
          </Grid>
          {!!numberCupsSelected && (
            <Grid item xs={12} lg={12}>
              {step !== -1 && (
                <Typography
                  component="p"
                  variant="subtitle2"
                >{`${numberCupsSelected} ${t(
                  `Gestiones.${
                    numberCupsSelected === 1
                      ? "Suministro seleccionado"
                      : "Suministros seleccionados"
                  }`
                )}`}</Typography>
              )}
            </Grid>
          )}
        </Grid>
      </Container>
      {(hasDetailCups || isLoading) && (
        <Container
          component="div"
          className="managements-header__cups-information"
          sx={{ display: "flex" }}
        >
          {isLoading ? (
            <Box padding={"0.5rem 0"} width={"100%"}>
              <LoadingComponentComponent
                isLoading={isLoading}
                position="relative"
                opacity={1}
              />
            </Box>
          ) : (
            detailCupsData.map(({ id, label, value, renderContent }) => (
              <Stack key={id} component="div">
                <Typography
                  component="span"
                  variant="body1"
                  className="information-label"
                >
                  {t(label)}
                </Typography>
                {renderContent ? (
                  renderContent(value)
                ) : (
                  <Typography component="span" variant="tab">
                    {value}
                  </Typography>
                )}
              </Stack>
            ))
          )}
        </Container>
      )}
    </Stack>
  );
};

export default ServicesUpdateHeader;
